import {
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  OAuthProvider,
  linkWithCredential,
  signOut,
  reauthenticateWithCredential,
  updatePassword,
  EmailAuthProvider,
  sendPasswordResetEmail,
} from 'firebase/auth';
import { auth } from '../firebase';
import api from './apiService';

// Friendly messages for Firebase Auth error codes
const AUTH_ERRORS = {
  'auth/invalid-credential': 'Invalid email or password',
  'auth/invalid-login-credentials': 'Invalid email or password',
  'auth/user-not-found': 'Invalid email or password',
  'auth/wrong-password': 'Invalid email or password',
  'auth/invalid-email': 'Please provide a valid email address',
  'auth/user-disabled': 'This account has been disabled. Contact your administrator.',
  'auth/too-many-requests': 'Too many failed attempts. Please try again later.',
  'auth/network-request-failed': 'Network error. Please check your connection.',
  'auth/weak-password': 'Password is too weak (minimum 6 characters)',
  'auth/requires-recent-login': 'Please sign in again before changing your password',
  'auth/popup-closed-by-user': 'The Google sign-in window was closed before completing sign-in',
  'auth/cancelled-popup-request': 'The Google sign-in window was closed before completing sign-in',
  'auth/popup-blocked': 'Your browser blocked the Google sign-in window. Allow pop-ups and try again.',
  'auth/account-exists-with-different-credential': 'An account already exists for this email with a different sign-in method',
  'auth/operation-not-allowed': 'This sign-in method is not enabled',
  'auth/unauthorized-domain': 'This domain is not authorized for sign-in',
};

const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({ prompt: 'select_account' });

const PROVIDERS = { google: googleProvider };
const PROVIDER_LABELS = { google: 'Google' };

// When a social sign-in collides with an existing password account, Firebase
// asks us to sign in with the existing method first and then link. We keep the
// pending credential here until the password sign-in succeeds.
let pendingLink = null;

export const describeAuthError = (error, fallback = 'Authentication failed') =>
  AUTH_ERRORS[error?.code] || error?.response?.data?.message || error?.message || fallback;

const handleApiError = (error, defaultMessage) => {
  const message = error.response?.data?.message || defaultMessage;
  const err = new Error(message);
  err.status = error.response?.status;
  err.data = error.response?.data;
  throw err;
};

export const authService = {
  /**
   * Sign in with Firebase Auth, then load the tenant profile from the API.
   * Accepts { email | login, password }.
   */
  async login(credentials) {
    const email = (credentials.email || credentials.login || '').trim();
    const { password } = credentials;
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      throw new Error(describeAuthError(error, 'Login failed'));
    }
    const linked = await this.completePendingLink();
    try {
      const response = await api.get('/auth/verify');
      return { message: linked ? `Login successful. ${linked.linked} sign-in is now linked to your account.` : 'Login successful', user: response.data.user, linked };
    } catch (error) {
      await signOut(auth).catch(() => {});
      handleApiError(error, 'Login failed');
    }
  },

  /**
   * Sign in with a social provider ('google'), then load the
   * tenant profile. The provider account's email must match an invited or
   * registered Excubia user.
   */
  async loginWithProvider(providerId) {
    const provider = PROVIDERS[providerId];
    const label = PROVIDER_LABELS[providerId] || providerId;
    if (!provider) throw new Error(`Unknown sign-in provider: ${providerId}`);
    try {
      await signInWithPopup(auth, provider);
    } catch (error) {
      if (error.code === 'auth/account-exists-with-different-credential') {
        const credential = OAuthProvider.credentialFromError(error);
        const email = error.customData && error.customData.email;
        pendingLink = { credential, email, label };
        const err = new Error(`An Excubia account already exists for ${email}. Sign in with your password once and ${label} will be linked to it.`);
        err.code = 'LINK_REQUIRED';
        err.email = email;
        throw err;
      }
      throw new Error(describeAuthError(error, `${label} sign-in failed`));
    }
    try {
      const response = await api.get('/auth/verify');
      return { message: 'Login successful', user: response.data.user };
    } catch (error) {
      await signOut(auth).catch(() => {});
      handleApiError(error, `${label} sign-in failed`);
    }
  },

  async loginWithGoogle() {
    return this.loginWithProvider('google');
  },

  /** After a password sign-in, link a pending social credential (if any). */
  async completePendingLink() {
    const user = auth.currentUser;
    if (!pendingLink || !user) return null;
    const link = pendingLink;
    pendingLink = null;
    if (link.email && user.email && link.email.toLowerCase() !== user.email.toLowerCase()) return null;
    try {
      await linkWithCredential(user, link.credential);
      return { linked: link.label };
    } catch (error) {
      console.warn('Failed to link provider:', error.code || error.message);
      return null;
    }
  },

  async activateInvitation(token) {
    try {
      const response = await api.post('/auth/activate-invitation', { token });
      return response.data;
    } catch (error) {
      handleApiError(error, 'Failed to accept invitation');
    }
  },

  async logout() {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      // Best effort - the local sign-out is what matters
    }
    await signOut(auth);
  },

  async verifyToken() {
    try {
      const response = await api.get('/auth/verify');
      return response.data;
    } catch (error) {
      handleApiError(error, 'Token verification failed');
    }
  },

  async getProfile() {
    try {
      const response = await api.get('/auth/profile');
      return response.data;
    } catch (error) {
      handleApiError(error, 'Failed to fetch profile');
    }
  },

  async updateProfile(profileData) {
    try {
      const response = await api.put('/auth/profile', profileData);
      return response.data;
    } catch (error) {
      handleApiError(error, 'Profile update failed');
    }
  },

  async changePassword(currentPassword, newPassword) {
    const user = auth.currentUser;
    if (!user || !user.email) throw new Error('Not authenticated');
    try {
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, newPassword);
      return { message: 'Password changed successfully' };
    } catch (error) {
      throw new Error(describeAuthError(error, 'Password change failed'));
    }
  },

  async requestPasswordReset(email) {
    try {
      await sendPasswordResetEmail(auth, email.trim());
      return { message: 'Password reset email sent' };
    } catch (error) {
      throw new Error(describeAuthError(error, 'Failed to send password reset email'));
    }
  },

  async getUsers() {
    try {
      const response = await api.get('/users');
      return response.data;
    } catch (error) {
      handleApiError(error, 'Failed to fetch users');
    }
  },
};

export default api;
